import { useMutation } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { assert } from '@/utils'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import { calculateFeeWithMinimalDenom, convertDenomToMicroDenom } from '@/wallet-utils'
import {
  IbcReturnType,
  generateIbcTimeoutTimestampBigInt,
  signIbcTransaction2,
  simulateIbcTransaction2
} from '@/wallet-utils/ibc'
import { stakeAmountAtom, stakeAutopilotRecipientAtom } from '../../atoms'
import { MutationParameters } from './types'
import { useSelectedDenom } from '../../../../contexts/denom'
import { useSelectedChain, useStrideChain, useChain } from '@/contexts/wallet'
import { useReferral } from '@/page-components/Stake/Referral'
import { ibc } from 'stridejs'

const useSignStakeAutopilotMutation = ({ setStakeAutopilotRaw }: MutationParameters) => {
  const { memo } = useReferral()

  const { address: strideAddress } = useStrideChain()

  const {
    address: selectedAddress,
    getSigningStargateClient,
    getAccount,
    getOfflineSigner,
    wallet
  } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const amount = useAtomValue(stakeAmountAtom)

  const stakeAutopilotRecipient = useAtomValue(stakeAutopilotRecipientAtom)

  const { address: recipientAddress } = useChain(stakeAutopilotRecipient)

  const handleMutation = async (): Promise<IbcReturnType> => {
    assert(strideAddress, 'You are unable to send token without connecting your Stride wallet.')

    assert(selectedAddress, `You are unable to send token without connecting your ${denom} wallet.`)

    assert(wallet, `You are unable to send token without your ${denom} wallet instance.`)

    const { coinMinimalDenom } = CHAIN_INFO_LIST[denom].stakeCurrency

    const amountInMicroDenom = convertDenomToMicroDenom(amount, coinMinimalDenom)

    const autopilot = JSON.stringify({
      autopilot: {
        stakeibc: {
          stride_address: strideAddress,
          action: 'LiquidStake',
          // Set ibc_receiver only for non-Stride destinations
          ibc_receiver: stakeAutopilotRecipient !== 'STRD' ? recipientAddress : undefined,
          // Provide transfer_channel only if:
          // 1. The recipient is not the selected wallet
          // 2. The recipient is not Stride
          transfer_channel: ![denom, 'STRD'].includes(stakeAutopilotRecipient)
            ? CHAIN_CONFIG[stakeAutopilotRecipient].withdrawChannel
            : undefined
        },
        receiver: strideAddress
      }
    })

    const transfer = ibc.applications.transfer.v1.MessageComposer.withTypeUrl.transfer({
      sourcePort: 'transfer',
      sourceChannel: CHAIN_CONFIG[denom].depositChannel,
      token: {
        amount: amountInMicroDenom.toString(),
        denom: CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom
      },
      sender: selectedAddress,
      receiver: strideAddress,
      memo: autopilot,
      timeoutHeight: {
        revisionHeight: 0n,
        revisionNumber: 0n
      },
      timeoutTimestamp: generateIbcTimeoutTimestampBigInt()
    })

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = { client, denom, account, offlineSigner: getOfflineSigner(), wallet: wallet.name }
    const gas = await simulateIbcTransaction2(ibcParams, selectedAddress, [transfer], '')
    const fee = calculateFeeWithMinimalDenom(coinMinimalDenom, gas)

    return await signIbcTransaction2(ibcParams, selectedAddress, [transfer], fee, memo)
  }

  const handleSuccess = (raw: IbcReturnType) => {
    setStakeAutopilotRaw(raw)
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignStakeAutopilotMutation }
